// The pricing engine: the only code that writes an amount. Approved test prices come from ../pricing.json.
// Membership ($99/year) and each scheduled HVAC visit ($49/$89/$139 for exactly 1/2/3 units) are separate purchases.
// Dues include no visit. Four or more units, or an unknown count, have no price: they need a scoped quote.
import table from '../pricing.json' with { type: 'json' };
import { OUTDOOR, type Facts } from './facts.ts';

export interface Pricing {
  status: 'approved_test';
  membership: { per_year: number; term: string };
  visit_by_units: Record<'1' | '2' | '3', number>;
  unresolved_charges: string;
  fixture_ids: { quote: string; membership: string; visit: string; receipt: string; case: string };
}

/** The approved test prices. Pure data: usable on the Mac and in the phone's bundle alike. */
export function loadPricing(): Pricing {
  return table as Pricing;
}

/** The member price of one scheduled visit for exactly 1, 2 or 3 units; null for anything else (no approved price). */
export function visitPrice(p: Pricing, units: number | null | undefined): number | null {
  return units === 1 || units === 2 || units === 3 ? p.visit_by_units[String(units) as '1'] : null;
}

/** Spending scenario for a membership year: $99 plus n separately purchased visits at one tier. Not an entitlement or a prediction. */
export function yearScenario(p: Pricing, units: 1 | 2 | 3, visits: number): number {
  return p.membership.per_year + visits * visitPrice(p, units)!;
}

/** What the household told us about units. A count is reported, never verified by the app; photos only suggest one. */
export type Reported = 1 | 2 | 3 | 'four_plus' | 'unknown';
export interface UnitBasis {
  reported: Reported | null;
  /** Outdoor units seen in photos: a suggestion the household may confirm, never inventory and never a price input on its own. */
  photoSuggested: number | null;
  verified: false;
  status: 'priced' | 'needs_count' | 'scoped_review';
}

export function photoSuggestion(facts: Facts | null): number | null {
  const outdoor = (facts?.equipment ?? []).filter((e) => OUTDOOR.has(e.kind) && e.confidence !== 'low');
  if (!outdoor.length) return null;
  const letters = new Set(outdoor.map((e) => e.system).filter((s) => s && s !== '?'));
  return Math.max(letters.size, 1);
}

export function unitBasis(reported: Reported | null | undefined, facts: Facts | null): UnitBasis {
  const r = reported ?? null;
  return {
    reported: r, photoSuggested: photoSuggestion(facts), verified: false,
    status: r === null ? 'needs_count' : r === 'four_plus' || r === 'unknown' ? 'scoped_review' : 'priced',
  };
}

export interface QuoteLine {
  kind: 'membership' | 'visit';
  label: string;
  /** null when no approved price exists for this scope (four or more units, unknown count). */
  amount: number | null;
  units?: Reported;
  note: string;
}

export interface Quote {
  ref: string;
  /** v1, v2...: any change to lines, amounts or terms after issue is a new version the household reviews again. */
  version: string;
  terms: string;
  kind: 'join' | 'visit';
  lines: QuoteLine[];
  /** Sum of the lines, or null while any line is unpriced: no executable purchase then. */
  total: number | null;
  unresolved: string[];
}

export const UNRESOLVED_TERMS = [
  '[applicable taxes or other quoted charges unresolved]',
  '[visit tasks, materials and unit definition unresolved]',
  '[payment timing, cancellation and refunds unresolved]',
  '[membership benefits and effective dates unresolved]',
];

const UNIT_WORD: Record<string, string> = { 1: 'one unit', 2: 'two units', 3: 'three units', four_plus: 'four or more units', unknown: 'units not yet known' };
export const unitsPhrase = (u: Reported | null | undefined) => (u ? UNIT_WORD[String(u)] : 'units not yet given');

/** A purchase quote: every line BOB offers. Joining always carries the membership line; with a unit count it also offers
 *  one separately chosen visit line. An existing member's visit quote never carries the annual fee again. */
export function buildQuote(p: Pricing, o: { kind: 'join' | 'visit'; units: Reported | null; ref: string; version: string; terms: string }): Quote {
  const lines: QuoteLine[] = [];
  if (o.kind === 'join') lines.push({ kind: 'membership', label: 'Club membership', amount: p.membership.per_year, note: 'per year · includes no visit' });
  if (o.kind === 'visit' || o.units !== null) {
    const amount = visitPrice(p, typeof o.units === 'number' ? o.units : null);
    lines.push({ kind: 'visit', label: `Scheduled HVAC visit, ${unitsPhrase(o.units)}`, amount, units: o.units ?? undefined,
                 note: amount === null ? 'needs a scoped quote · no approved price' : 'per visit · member price' });
  }
  const total = lines.length && lines.every((l) => l.amount !== null) ? lines.reduce((s, l) => s + l.amount!, 0) : null;
  return { ref: o.ref, version: o.version, terms: o.terms, kind: o.kind, lines, total, unresolved: UNRESOLVED_TERMS };
}

/** The lines the household has chosen from a quote and their total. Joining without a visit is membership alone. */
export function selection(q: Quote, includeVisit: boolean): { lines: QuoteLine[]; total: number | null } {
  const lines = q.lines.filter((l) => l.kind === 'membership' || (l.kind === 'visit' && (includeVisit || q.kind === 'visit')));
  const total = lines.length && lines.every((l) => l.amount !== null) ? lines.reduce((s, l) => s + l.amount!, 0) : null;
  return { lines, total };
}

/** Whether a quote could be authorized in this research prototype: complete lines and a computed total. */
export function executable(q: Quote | null, includeVisit: boolean): { ok: boolean; reason?: string } {
  if (!q) return { ok: false, reason: 'nothing to authorize' };
  const s = selection(q, includeVisit);
  if (!s.lines.length) return { ok: false, reason: 'nothing to authorize' };
  if (s.total === null) return { ok: false, reason: 'a chosen line has no approved price and needs a scoped quote' };
  return { ok: true };
}

/** Same lines and amounts and terms. */
export function sameQuote(a: Quote | null, b: Quote | null): boolean {
  if (!a || !b) return a === b;
  return a.terms === b.terms && a.total === b.total && JSON.stringify(a.lines) === JSON.stringify(b.lines);
}
